/**
 * EventDetailModal - ActivityDetailPopup-inspired Edition
 *
 * A centered glassmorphic popup for themed calendar exploration.
 * First page: Calendar overview with emoji, title, stats
 * Following pages: Individual event details with hero image + content panel
 *
 * Styled to match the ActivityDetailPopup vibe: blurred overlay,
 * rounded card, Bricolage Grotesque typography, warm aesthetic.
 */

import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon, Loader } from 'semantic-ui-react';
import { useEventModalStore } from '../../stores/useEventModalStore';
import { getTextColorForBackground } from '../../config/calendarColors';
import { useEventModalUrl } from '../../hooks/useEventModalUrl';
import { FloatingEmojiLayer } from '../UI/FloatingEmojis';
import CalendarOverview from './CalendarOverview';
import EventDetailContent from './EventDetailContent';
import EventCarousel from './EventCarousel';
import './EventDetailModal.scss';

// Helper to darken a hex color
const darkenColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

const EventDetailModal = () => {
  const {
    isOpen,
    currentEvent,
    currentCalendar,
    currentIndex,
    allEvents,
    isLoadingCalendar,
    closeModal,
    nextEvent,
    prevEvent,
    goToEvent,
  } = useEventModalStore();

  // Sync modal state with URL for deep linking
  useEventModalUrl();

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowRight':
        nextEvent();
        break;
      case 'ArrowLeft':
        prevEvent();
        break;
      default:
        break;
    }
  }, [isOpen, closeModal, nextEvent, prevEvent]);

  // Set up keyboard listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  // Show loading state while calendar data is being fetched
  if (isLoadingCalendar || !currentCalendar) {
    return createPortal(
      <div className="event-detail-modal-overlay">
        <div className="event-detail-modal loading">
          <div className="modal-loading-overlay">
            <Loader active size="large">
              Loading calendar details...
            </Loader>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Calculate theme colors
  const accentColor = currentCalendar.color || '#B03060';
  const accentDark = darkenColor(accentColor, 20);
  const textColor = getTextColorForBackground(accentColor);
  const isLightAccent = textColor !== '#ffffff';

  // CSS custom properties for theming - make the color DOMINANT
  const themeStyles = {
    '--modal-accent': accentColor,
    '--modal-accent-dark': accentDark,
    '--modal-text': textColor,
    '--modal-accent-muted': isLightAccent
      ? `${accentColor}cc`
      : `${accentColor}ee`,
  };

  // Total slides = 1 (overview) + number of events
  const totalSlides = allEvents.length + 1;
  // Current slide: -1 = overview (slide 0), 0+ = events (slides 1+)
  const currentSlide = currentIndex + 1;

  const handleDotClick = (slideIndex) => {
    if (slideIndex === 0) {
      goToEvent(-1);
    } else {
      goToEvent(slideIndex - 1);
    }
  };

  const modalContent = (
    <div
      className="event-detail-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      {/* Floating emojis on the blurred background - like ActivityDetailPopup */}
      <FloatingEmojiLayer emoji={currentCalendar.emoji} count={6} />

      <div
        className="event-detail-modal"
        style={themeStyles}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
      >
        {/* Close button */}
        <button
          className="event-modal-close"
          onClick={closeModal}
          aria-label="Close modal"
        >
          <Icon name="close" />
        </button>

        {/* Navigation arrows only */}
        <EventCarousel
          currentIndex={currentSlide}
          totalSlides={totalSlides}
          onPrev={prevEvent}
          onNext={nextEvent}
          onDotClick={handleDotClick}
          hideDots
        />

        {/* Main content */}
        <div className="event-modal-content">
          {currentIndex === -1 ? (
            <CalendarOverview calendar={currentCalendar} />
          ) : (
            <EventDetailContent
              event={currentEvent}
              calendar={currentCalendar}
              eventIndex={currentIndex}
              totalEvents={allEvents.length}
            />
          )}
        </div>
      </div>

      {/* Carousel dots below the popup */}
      {totalSlides > 1 && (
        <div className="carousel-dots-external" style={themeStyles} onClick={(e) => e.stopPropagation()}>
          {Array.from({ length: totalSlides }, (_, i) => (
            <button
              key={i}
              className={`carousel-dot ${i === 0 ? 'overview-dot' : ''} ${currentSlide === i ? 'active' : ''}`}
              onClick={() => handleDotClick(i)}
              role="tab"
              aria-selected={currentSlide === i}
              aria-label={i === 0 ? 'Calendar overview' : `Event ${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Render in portal to ensure proper stacking
  return createPortal(modalContent, document.body);
};

export default EventDetailModal;
